import React, { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  CompoundDto,
  CreateProductCatalogDto,
  IsotopeDto,
  ProductCatalogDto,
  ProductFamilyDto,
  ProductType,
} from '@services/dtos'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Alert, Grid } from '@mui/material'
import { getProductFamilies, setProductFamilyFilters } from '@state/reducers/productFamilyReducer'
import { ProductTypeOptions } from '@utils/constants'
import { getCompounds, setCompoundFilters } from '@state/reducers/compoundReducer'
import ProductFamilyForm from '../../ProductFamilyForm'
import CompoundForm from '../../CompoundForm'
import InputField from '@components/inputs/InputField'
import SelectField from '@components/inputs/SelectField'
import { AddWrapperInput } from '@components/inputs/AddWrapperInput'
import { CheckBoxInput } from '@components/inputs'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { trim } from 'lodash'
import IsotopeForm from '../../IsotopeForm'
import { isotopeService } from '@services/IsotopeService'

type Props = {
  useCreateForm: UseFormReturn<CreateProductCatalogDto, object>
  editingProductCatalog?: ProductCatalogDto | null
  duplicateCase?: boolean
}

const ProductCatalogSectionInfo: React.FC<Props> = ({ useCreateForm: createForm, editingProductCatalog, duplicateCase }) => {
  const { control, watch, setValue, formState: { errors } } = createForm
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(setProductFamilyFilters({ search: undefined }))
      dispatch(setCompoundFilters({ search: undefined }))
    }
  }, [dispatch])

  const fetchProductFamilies = async (search: string) => {
    dispatch(setProductFamilyFilters({ search }))
    const page = await dispatch(getProductFamilies()).unwrap()
    return page?.datas ?? []
  }
  const fetchCompounds = async (search: string) => {
    dispatch(setCompoundFilters({ search }))
    const page = await dispatch(getCompounds()).unwrap()
    return page?.datas ?? []
  }
  const fetchIsotopes = async (search: string) => {
    const page = await isotopeService.getAll({ search })
    return page?.datas ?? []
  }

  const type = watch('type')
  const isDivisible = [ProductType.READY_MADE, ProductType.KIT].includes(type)

  const fields1 = [
    { name: 'productCode', label: 'Code', sm: 3 },
    { name: 'name', label: 'Nom', sm: 9 },
    { name: 'genericName', label: 'DCI', sm: isDivisible ? 6 : 9 },
  ]
  const fields2 = [
    { name: 'inUseStabilityMinCold', label: 'Stabilité à l\'usage (min) - froid', type: 'number', md: 4 },
    { name: 'inUseStabilityMinRoom', label: 'Stabilité à l\'usage (min) - ambiant', type: 'number', md: 4 },
    { name: 'excursionOutOfColdMaxMin', label: 'Excursion hors froid max (min)', type: 'number', md: 4 },
    { name: 'notes', label: 'Notes', type: 'textarea', sm: 12 },
  ]

  return (
    <>
      {duplicateCase && (trim(editingProductCatalog?.productCode) === trim(watch('productCode')) 
        || trim(editingProductCatalog?.name) === trim(watch('name'))) && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Le code et le nom doivent être différents du produit original.
          </Alert>
        </Grid>
      )}
      {fields1.map((field) => (
        <Grid item xs={12} sm={field.sm} key={field.name}>
          <InputField
            control={control}
            name={field.name}
            label={field.label}
            {...(duplicateCase && (field.name === 'productCode' || field.name === 'name') 
              && trim(watch(field.name)) === trim(editingProductCatalog?.[field.name])
              ? { color: 'warning', focused: true }
              : {})}
          />
        </Grid>
      ))}

      <Grid item xs={12} sm={3}>
        <SelectField
          control={control}
          name={'type'}
          options={ProductTypeOptions}
          value={type}
          label={'Type'}
          data-cy="select-type"
        />
      </Grid>

      {isDivisible && (
        <Grid item xs={12} md={3}>
          <InputField
            control={control}
            name={'maxDivision'}
            label={'Division maximale'}
            type={'number'}
          />
        </Grid>
      )}

      {type === ProductType.GENERATOR && (
        <>
          <Grid item xs={12} sm={6}>
            <AddWrapperInput
              legend="Isotope père"
              addTooltipTitle="Ajouter un isotope"
              dialogContent={(close) => (
                <IsotopeForm onClose={close} />
              )}
              input={(<ServerAutocomplete<IsotopeDto>
                fetchService={fetchIsotopes}
                onChange={(value: IsotopeDto | null) => {
                  setValue('generatorParentIsotopeId', value?.id)
                }}
                getValue={(value) => value?.id?.toString() || null}
                getOptionLabel={(value) => {
                  const isotope = value as IsotopeDto
                  return isotope ? isotope.name : ''
                }}
                debounceMs={300}
                placeholder="Rechercher un isotope"
                label="Isotope père"
                helperText={errors.generatorParentIsotopeId?.message}
                error={!!errors.generatorParentIsotopeId}
                minSearchLength={0}
                initialOptions={editingProductCatalog?.generatorParentIsotope ? [editingProductCatalog.generatorParentIsotope] : []}
                value={editingProductCatalog?.generatorParentIsotope ?? null}
              />)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <AddWrapperInput
              legend="Isotope fils"
              addTooltipTitle="Ajouter un isotope"
              dialogContent={(close) => (
                <IsotopeForm onClose={close} />
              )}
              input={(<ServerAutocomplete<IsotopeDto>
                fetchService={fetchIsotopes}
                onChange={(value: IsotopeDto | null) => {
                  setValue('generatorDaughterIsotopeId', value?.id)
                }}
                getValue={(value) => value?.id?.toString() || null}
                getOptionLabel={(value) => {
                  const isotope = value as IsotopeDto
                  return isotope ? isotope.name : ''
                }}
                debounceMs={300}
                placeholder="Rechercher un isotope"
                label="Isotope fils"
                helperText={errors.generatorDaughterIsotopeId?.message}
                error={!!errors.generatorDaughterIsotopeId}
                minSearchLength={0}
                initialOptions={editingProductCatalog?.generatorDaughterIsotope ? [editingProductCatalog.generatorDaughterIsotope] : []}
                value={editingProductCatalog?.generatorDaughterIsotope ?? null}
              />)}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={6}>
        <AddWrapperInput
          legend="Famille de produits"
          addTooltipTitle="Ajouter une famille de produits"
          dialogContent={(close) => (
            <ProductFamilyForm onClose={close} />
          )}
          onAfterClose={() => {
            dispatch(getProductFamilies())
          }}
          input={(<ServerAutocomplete<ProductFamilyDto>
            fetchService={fetchProductFamilies}
            onChange={(value: ProductFamilyDto | null) => {
              setValue('primaryFamilyId', value?.id)
            }}
            getValue={(value) => value?.id?.toString() || null}
            getOptionLabel={(value) => {
              const productFamily = value as ProductFamilyDto
              return productFamily ? productFamily.name : ''
            }}
            debounceMs={300}
            placeholder="Rechercher une famille de produits"
            label="Famille de produits"
            helperText={errors.primaryFamilyId?.message}
            error={!!errors.primaryFamilyId}
            minSearchLength={0}
            initialOptions={editingProductCatalog?.productFamily ? [editingProductCatalog.productFamily] : []}
            value={editingProductCatalog?.productFamily ?? null}
          />)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <AddWrapperInput
          legend="MRP"
          addTooltipTitle="Ajouter un MRP"
          dialogContent={(close) => (
            <CompoundForm onClose={close} />
          )}
          onAfterClose={() => {
            dispatch(getCompounds())
          }}
          input={(<ServerAutocomplete<CompoundDto>
            fetchService={fetchCompounds}
            onChange={(value: CompoundDto | null) => {
              setValue('compoundId', value?.id)
            }}
            getValue={(value) => value?.id?.toString() || null}
            getOptionLabel={(value) => {
              const compound = value as CompoundDto
              return compound ? compound.name : ''
            }}
            debounceMs={300}
            placeholder="Rechercher un MRP"
            label="MRP"
            helperText={errors.compoundId?.message}
            error={!!errors.compoundId}
            minSearchLength={0}
            initialOptions={editingProductCatalog?.compound ? [editingProductCatalog.compound] : []}
            value={editingProductCatalog?.compound ?? null}
          />)}
        />
      </Grid>

      <Grid item xs={12}>
        <CheckBoxInput
          control={control}
          name="requiresColdChain"
          label="Nécessite la chaîne du froid" />
      </Grid>

      {fields2.map((field) => (
        <Grid item xs={12} sm={field.sm ? field.sm : 6} md={field.md} key={field.name}>
          <InputField
            control={control}
            name={field.name}
            label={field.label}
            type={field.type ? field.type : 'text'}
            multiline={field.type === 'textarea'}
            rows={field.type === 'textarea' ? 2 : undefined}
          />
        </Grid>
      ))}
    </>
  )
}

export default ProductCatalogSectionInfo
